<template>
  <div class="header-container">
    <div class="header-container-logo">
      <img :src="logoSrc" alt="Logo"/>
    </div>

    <div class="header-container-navigation-item">
      <nav>
        <a
            v-for="link in navigationLinks"
            :key="link.path"
            :href="link.disabled ? null : link.path"
            :class="{
              'active-link': isActive(link.path),
              'disabled-link': link.disabled
            }"
            rel="noopener noreferrer"
            class="navigation-link"
            :tabindex="link.disabled ? -1 : 0"
        >
          {{ link.name }}
        </a>

      </nav>
    </div>

    <div class="header-container-login-item" v-if="!isAuthenticated">
      <a href="/login" class="header-container-item-login-link">Войти на сайт</a>
    </div>
    <div class="header-container-item-profile" v-else>
      <div class="profile-row">
        <a href="/profile">
          <img :src="profilePictureUrl" alt="Profile Picture" class="profile-picture"/>
        </a>
        <span>{{ user.name }}</span>
        <button @click="logoutAction" class="logout-button">Выйти</button>
      </div>
      <p class="last-update-text">{{ lastUpdateDate }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LogoImage from '@/assets/images/am_logo 1.svg'
import ProfilePicture from '@/assets/images/profile-picture-example.svg'
import { generateDatalensToken, getLastDatasetUpdateDate, logout } from '@/utils/requests'
import { useStore } from 'vuex'

const route = useRoute()
const store = useStore()
const router = useRouter()

const logoSrc = ref(LogoImage)
const navigationLinks = ref([
  { name: 'СМИ', path: 'https://automarketolog.ru/' },
  { name: 'Аналитика', path: '/analytics' },
  { name: 'Поиск по VIN', path: '/analytics1', disabled: true },
  { name: 'ИНН поиск', path: '/analytics2', disabled: true },
])

const user = computed(() => store.getters['main/userInfo'] || {})

const isAuthenticated = computed(() => !!localStorage.getItem('token'))

const profilePictureUrl = computed(() => {
  return user.value?.profile_picture
      ? `${process.env.VUE_APP_URL_DEV}/storage/${user.value.profile_picture}`
      : ProfilePicture
})

const lastUpdateDateText = 'Дата последнего обновления: '
const lastUpdateDate = ref('')

const isActive = path => route.path.startsWith(path)

const fetchUser = id => store.dispatch('main/fetchUser', id)

const logoutAction = async () => {
  try {
    const response = await logout(localStorage.getItem('token'))
    if (response.ok) {
      localStorage.clear()
      await store.dispatch('main/logout')
      await router.push('/login')
    } else {
      console.error('Ошибка при выходе из системы:', response.statusText)
    }
  } catch (error) {
    console.error('Ошибка при выполнении запроса:', error)
  }
}

const getLastUpdateDate = async () => {
  try {
    const token = localStorage.getItem('token')

    const tokenResponse = await generateDatalensToken(token)
    const tokenJsonData = await tokenResponse.json()
    if (!tokenResponse.ok || !tokenJsonData.data.token) {
      throw new Error('Ошибка при получении токена даталенс:', tokenResponse.status);
    }
    const datalensToken = tokenJsonData.data.token;

    const response = await getLastDatasetUpdateDate(token, datalensToken)
    const responseJsonData = await response.json()
    if (response.ok) {
      console.log(responseJsonData)
      lastUpdateDate.value = lastUpdateDateText + formatDate(new Date(responseJsonData.data.last_update_date))
    } else {
      throw new Error('Ошибка при получении последней даты обновления датасетов: ' + response.status)
    }
  } catch (error) {
    console.error('Ошибка при выполнении запроса:', error)
    lastUpdateDate.value = lastUpdateDateText + formatDate(new Date())
  }
}

const formatDate = (date) => {
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  })
}

onMounted(async () => {
  await fetchUser(user.value?.id)
  await getLastUpdateDate()
})
</script>

<style scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 96px;
  border-bottom: 1px solid rgba(87, 90, 96, 0.2);
}

.header-container-navigation-item nav a {
  margin: 0 12px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  color: #23282C;
  line-height: 16px;
}

.header-container-navigation-item nav a:hover {
  cursor: pointer;
  color: red;
}

.header-container-navigation-item nav a.active-link {
  color: #198FA0;
  border-bottom: 2px solid #198FA0;
  padding-bottom: 38px;
}

.header-container-navigation-item nav a.disabled-link {
  color: gray;
  cursor: not-allowed;
}

.header-container-login-item {
  height: 56px;
  border-radius: 4px;
  padding: 12px 20px;
  border: 1px solid #23282C;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-container-item-login-link {
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  color: #23282C;
  text-decoration: none;
}

.header-container-item-profile {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.profile-row {
  display: flex;
  align-items: center;
}

.header-container-item-profile span {
  margin-left: 12px;
  font-weight: 400;
  font-size: 16px;
  color: #23282C;
}

.profile-picture {
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

.logout-button {
  margin-left: 12px;
  background-color: #198FA0;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
}

.logout-button:hover {
  background-color: #136f80;
}

.last-update-text {
  margin-top: 4px;
  font-size: 12px;
  color: gray;
}
</style>
